import { Trans } from "@lingui/macro";
import cx from "classnames";
import { Link, useLocation } from "react-router-dom";

import { useTheme } from "context/ThemeContext/ThemeContext";

import { UsdxMark } from "components/UsdxMark/UsdxMark";

const NAV_ITEMS = [
  { label: "Trade", to: "/terminal" },
  { label: "Portfolio", to: "/accounts" },
  { label: "DEX", to: "/swap" },
  { label: "Pools", to: "/pools" },
  { label: "Earn", to: "/earn" },
];

const THEME_MODES = [
  {
    mode: "system" as const,
    title: "System",
    icon: (
      <>
        <rect x="2" y="4" width="20" height="13" rx="2" />
        <path d="M8 21h8" />
        <path d="M12 17v4" />
      </>
    ),
  },
  {
    mode: "light" as const,
    title: "Light",
    icon: (
      <>
        <circle cx="12" cy="12" r="4.2" />
        <path d="M12 2v2.2M12 19.8V22M2 12h2.2M19.8 12H22M4.9 4.9l1.6 1.6M17.5 17.5l1.6 1.6M19.1 4.9l-1.6 1.6M6.5 17.5l-1.6 1.6" />
      </>
    ),
  },
  {
    mode: "dark" as const,
    title: "Dark",
    icon: <path d="M20.5 14.4A8.6 8.6 0 1 1 9.6 3.5a6.9 6.9 0 0 0 10.9 10.9Z" />,
  },
];

export function TerminalHeader() {
  const { themeMode, setThemeMode } = useTheme();
  const { pathname } = useLocation();

  return (
    <header
      className="t-anim-fade flex flex-wrap items-center justify-between gap-x-24 gap-y-12 px-12 py-12 sm:px-20 sm:py-14"
      style={{ background: "var(--t-hdr)", boxShadow: "var(--t-hdr-shadow)" }}
    >
      <Link to="/terminal" className="flex shrink-0 items-center gap-8 sm:gap-11">
        <UsdxMark size={38} />
        <span className="t-display text-24 leading-1 tracking-[3px] text-white sm:text-[29px] sm:tracking-[5px]">
          USDX
        </span>
      </Link>

      {/*
       * On a phone the nav drops onto its own full-width row and scrolls
       * sideways, rather than wrapping the header into a tall stack.
       */}
      <nav className="t-nav order-last flex w-full items-center gap-6 scrollbar-hide sm:order-none sm:w-auto sm:flex-wrap sm:overflow-visible">
        {NAV_ITEMS.map((item) => {
          const isActive = pathname === item.to || pathname.startsWith(`${item.to}/`);

          return (
            <Link
              key={item.to}
              to={item.to}
              className={cx(
                "shrink-0 whitespace-nowrap rounded-full px-12 py-8 text-14 font-medium tracking-[0.3px] transition-colors sm:px-15",
                isActive ? "bg-white/20 text-white" : "text-white/80 hover:bg-white/10 hover:text-white"
              )}
            >
              {item.label}
            </Link>
          );
        })}
      </nav>

      <div className="flex min-w-0 flex-wrap items-center gap-y-10 gap-x-8 md:gap-x-10">
        <div className="t-glass flex items-center gap-3 rounded-full p-3">
          {THEME_MODES.map((item) => {
            const isActive = themeMode === item.mode;

            return (
              <button
                key={item.mode}
                type="button"
                title={item.title}
                aria-label={item.title}
                aria-pressed={isActive}
                onClick={() => setThemeMode(item.mode)}
                className={cx(
                  "grid h-30 w-30 place-items-center rounded-full transition-all",
                  isActive ? "bg-white text-[color:var(--t-hdr-ink)]" : "bg-transparent text-white/80"
                )}
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {item.icon}
                </svg>
              </button>
            );
          })}
        </div>

        <button
          type="button"
          className="t-lift whitespace-nowrap rounded-full px-16 py-11 text-13 font-semibold text-white transition-[transform,box-shadow] duration-200 md:px-22 md:text-14"
          style={{ background: "var(--t-cta)", boxShadow: "var(--t-wallet-shadow)" }}
        >
          <Trans>Connect Wallet</Trans>
        </button>

        {/* Language is the first thing to go when the header runs out of room. */}
        <button
          type="button"
          className="t-glass hidden items-center gap-7 whitespace-nowrap rounded-full px-12 py-9 text-13 text-white sm:flex md:px-15"
        >
          English <span className="text-[10px]">▾</span>
        </button>
      </div>
    </header>
  );
}
